import { useState } from 'react';
import { createPortal } from 'react-dom';
import { Navigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Spinner, PageSkeleton } from '@daycal/ui';
import {
  FiUsers, FiUserPlus, FiKey, FiPause, FiPlay, FiTrash2, FiCopy, FiCheck, FiAlertCircle, FiX, FiMail,
} from 'react-icons/fi';
import { api } from '../lib/apiClient.js';
import { useAuth } from '../contexts/AuthContext.jsx';

// Authority management dashboard — Pillar C/D (PRD §8, §9). Create the agency, then provision
// and control employee logins. Employees are created by the boss (no email, no self-signup).
export default function Team() {
  const { profile, isEmployee } = useAuth();
  const { data: ctx, isLoading } = useQuery({
    queryKey: ['agencyContext'],
    queryFn: api.agencyContext,
    enabled: !isEmployee,
  });

  // Employees have no management surface — send them to their own schedule (PRD §5).
  if (isEmployee) return <Navigate to="/availability" replace />;
  if (isLoading) return <PageSkeleton />;
  if (!profile?.allow_teams) return <UpsellCard />;
  if (!ctx?.agency || ctx.role !== 'owner') return <CreateAgencyCard />;
  return <EmployeesManager agency={ctx.agency} />;
}

function UpsellCard() {
  return (
    <div className="animate-dc-rise mx-auto max-w-2xl">
      <div className="rounded-3xl bg-white p-10 text-center shadow-[0_8px_30px_rgb(0,0,0,0.12)] border border-slate-100">
        <div className="mx-auto mb-4 grid h-12 w-12 place-items-center rounded-2xl bg-slate-900 text-white">
          <FiUsers className="h-6 w-6" />
        </div>
        <h1 className="text-2xl font-bold text-slate-900">Team booking is a paid feature</h1>
        <p className="mx-auto mt-2 max-w-md text-sm text-slate-500">
          Turn your account into an agency: create logins for your team, share one booking link,
          and let the same slot be booked once per free agent. Upgrade to unlock seats.
        </p>
        <a href="/upgrade" className="mt-6 inline-flex h-11 items-center rounded-full bg-slate-900 px-8 text-sm font-semibold text-white hover:bg-slate-800">
          View plans
        </a>
      </div>
    </div>
  );
}

function CreateAgencyCard() {
  const qc = useQueryClient();
  const [form, setForm] = useState({ name: '', slug: '', brand_name: '' });
  const [error, setError] = useState('');
  const create = useMutation({
    mutationFn: () => api.createAgency({
      name: form.name,
      slug: form.slug,
      brand_name: form.brand_name || undefined,
    }),
    onSuccess: () => { setError(''); qc.invalidateQueries({ queryKey: ['agencyContext'] }); },
    onError: (err) => setError(err.details?.map((d) => `${d.path}: ${d.message}`).join(', ') || err.message),
  });

  return (
    <div className="animate-dc-rise mx-auto max-w-2xl">
      <div className="rounded-3xl bg-white p-8 md:p-10 shadow-[0_8px_30px_rgb(0,0,0,0.12)] border border-slate-100">
        <h1 className="text-2xl font-bold text-slate-900">Create your agency</h1>
        <p className="mt-1 mb-8 text-sm text-slate-500">
          Pick a public handle for your team. Your agents sign in at the normal
          {' '}<span className="font-mono font-semibold text-slate-700">/login</span> page with the
          email &amp; password you give them.
        </p>

        <div className="grid gap-6">
          <label className="block">
            <span className="mb-2 block text-[10px] font-bold uppercase tracking-wider text-slate-700">Agency name</span>
            <input
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              placeholder="Acme Sales"
              className="w-full h-11 rounded-xl border border-slate-400 bg-slate-50 px-4 text-sm font-medium text-slate-700 outline-none focus:border-slate-300 focus:bg-white focus:ring-2 focus:ring-slate-100"
            />
          </label>
          <label className="block">
            <span className="mb-2 block text-[10px] font-bold uppercase tracking-wider text-slate-700">Handle (slug)</span>
            <input
              value={form.slug}
              onChange={(e) => setForm((f) => ({ ...f, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') }))}
              placeholder="acme-sales"
              className="w-full h-11 rounded-xl border border-slate-400 bg-slate-50 px-4 font-mono text-sm font-medium text-slate-700 outline-none focus:border-slate-300 focus:bg-white focus:ring-2 focus:ring-slate-100"
            />
          </label>
          <label className="block">
            <span className="mb-2 block text-[10px] font-bold uppercase tracking-wider text-slate-700">Brand name (optional)</span>
            <input
              value={form.brand_name}
              onChange={(e) => setForm((f) => ({ ...f, brand_name: e.target.value }))}
              placeholder="Acme"
              className="w-full h-11 rounded-xl border border-slate-400 bg-slate-50 px-4 text-sm font-medium text-slate-700 outline-none focus:border-slate-300 focus:bg-white focus:ring-2 focus:ring-slate-100"
            />
          </label>
        </div>

        {error && <p className="mt-4 text-sm text-red-600">{error}</p>}

        <div className="mt-8 flex justify-end border-t border-slate-100 pt-8">
          <button
            onClick={() => create.mutate()}
            disabled={create.isPending || !form.name || form.slug.length < 1}
            className="flex h-11 items-center justify-center rounded-full bg-slate-900 px-8 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-50"
          >
            {create.isPending ? <Spinner /> : 'Create agency'}
          </button>
        </div>
      </div>
    </div>
  );
}

function EmployeesManager({ agency }) {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ['employees'], queryFn: api.listEmployees });
  const [addOpen, setAddOpen] = useState(false);
  const [newCreds, setNewCreds] = useState(null);   // { email, username, password } shown once
  const [resetFor, setResetFor] = useState(null);   // member being reset
  const [emailFor, setEmailFor] = useState(null);   // member whose email is being edited

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['employees'] });
    qc.invalidateQueries({ queryKey: ['agencyContext'] });
  };

  if (isLoading) return <PageSkeleton />;
  const employees = data?.employees || [];
  const seats = data?.seats || { used: 0, max: 0 };
  const seatsFull = seats.max !== -1 && seats.used >= seats.max;

  return (
    <div className="animate-dc-rise mx-auto max-w-6xl space-y-6">
      {/* Header / portal card */}
      <div className="rounded-3xl bg-white p-8 md:p-10 shadow-[0_8px_30px_rgb(0,0,0,0.12)] border border-slate-100">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">{agency.name}</h1>
            <p className="mt-1 text-sm text-slate-500">
              Agents sign in at <span className="font-mono font-semibold text-slate-700">{window.location.origin}/login</span> with their email &amp; password.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <span className="rounded-full bg-slate-100 px-4 py-1.5 text-xs font-bold text-slate-600">
              {seats.used}{seats.max === -1 ? '' : ` / ${seats.max}`} seats used
            </span>
            <button
              onClick={() => setAddOpen(true)}
              disabled={seatsFull}
              className="inline-flex h-11 items-center gap-2 rounded-full bg-slate-900 px-6 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-50"
              title={seatsFull ? 'Seat limit reached — upgrade to add more' : ''}
            >
              <FiUserPlus className="h-4 w-4" /> Add employee
            </button>
          </div>
        </div>
      </div>

      {/* Roster */}
      <div className="rounded-3xl bg-white p-8 md:p-10 shadow-[0_8px_30px_rgb(0,0,0,0.12)] border border-slate-100 min-h-[240px]">
        <h2 className="mb-6 text-lg font-bold text-slate-900">Employees</h2>
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm text-slate-600">
            <thead className="border-b border-slate-100 bg-slate-50 text-[10px] font-bold uppercase tracking-wider text-slate-500">
              <tr>
                <th className="rounded-tl-lg px-6 py-4 font-medium">Name</th>
                <th className="px-6 py-4 font-medium">Email (login)</th>
                <th className="px-6 py-4 font-medium">Username</th>
                <th className="px-6 py-4 font-medium">Status</th>
                <th className="px-6 py-4 font-medium">Calendar</th>
                <th className="rounded-tr-lg px-6 py-4 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {employees.length === 0 ? (
                <tr>
                  <td colSpan="6" className="py-12 text-center text-sm italic text-slate-400">
                    No employees yet. Add your first agent to get started.
                  </td>
                </tr>
              ) : (
                employees.map((m) => (
                  <EmployeeRow
                    key={m.id}
                    member={m}
                    onReset={() => setResetFor(m)}
                    onEditEmail={() => setEmailFor(m)}
                    onChanged={invalidate}
                  />
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {addOpen && (
        <AddEmployeeModal
          onClose={() => setAddOpen(false)}
          onCreated={(creds) => { setAddOpen(false); if (creds?.password) setNewCreds(creds); invalidate(); }}
        />
      )}
      {newCreds && <CredentialsModal creds={newCreds} onClose={() => setNewCreds(null)} />}
      {resetFor && (
        <ResetPasswordModal member={resetFor} onClose={() => setResetFor(null)} onDone={() => { setResetFor(null); invalidate(); }} />
      )}
      {emailFor && (
        <EditEmailModal member={emailFor} onClose={() => setEmailFor(null)} onDone={() => { setEmailFor(null); invalidate(); }} />
      )}
    </div>
  );
}

function EmployeeRow({ member: m, onReset, onEditEmail, onChanged }) {
  const suspend = useMutation({ mutationFn: () => api.suspendEmployee(m.id), onSuccess: onChanged });
  const reactivate = useMutation({ mutationFn: () => api.reactivateEmployee(m.id), onSuccess: onChanged });
  const remove = useMutation({ mutationFn: () => api.removeEmployee(m.id), onSuccess: onChanged });
  const suspended = m.status === 'suspended';

  return (
    <tr className="border-b border-slate-50 hover:bg-slate-50/50">
      <td className="px-6 py-4 font-semibold text-slate-900">{m.display_name}</td>
      <td className="px-6 py-4 text-slate-600">{m.email}</td>
      <td className="px-6 py-4 font-mono text-slate-600">{m.username}</td>
      <td className="px-6 py-4">
        <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[10px] font-bold ${suspended ? 'bg-amber-50 text-amber-600' : 'bg-emerald-50 text-emerald-600'}`}>
          {suspended ? 'Suspended' : 'Active'}
        </span>
      </td>
      <td className="px-6 py-4">
        <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[10px] font-bold ${m.calendar_connected ? 'bg-slate-100 text-slate-600' : 'bg-red-50 text-red-500'}`}>
          {m.calendar_connected ? 'Connected' : 'Not connected'}
        </span>
      </td>
      <td className="px-6 py-4">
        <div className="flex items-center justify-end gap-2">
          <button onClick={onEditEmail} title="Change email" className="rounded-full p-2 text-slate-400 hover:bg-slate-100 hover:text-slate-700">
            <FiMail className="h-4 w-4" />
          </button>
          <button onClick={onReset} title="Reset password" className="rounded-full p-2 text-slate-400 hover:bg-slate-100 hover:text-slate-700">
            <FiKey className="h-4 w-4" />
          </button>
          {suspended ? (
            <button onClick={() => reactivate.mutate()} title="Reactivate" className="rounded-full p-2 text-slate-400 hover:bg-emerald-50 hover:text-emerald-600">
              <FiPlay className="h-4 w-4" />
            </button>
          ) : (
            <button onClick={() => suspend.mutate()} title="Suspend" className="rounded-full p-2 text-slate-400 hover:bg-amber-50 hover:text-amber-600">
              <FiPause className="h-4 w-4" />
            </button>
          )}
          <button
            onClick={() => { if (confirm(`Remove ${m.display_name}? This frees the seat and revokes access.`)) remove.mutate(); }}
            title="Remove"
            className="rounded-full p-2 text-slate-400 hover:bg-red-50 hover:text-red-500"
          >
            <FiTrash2 className="h-4 w-4" />
          </button>
        </div>
      </td>
    </tr>
  );
}

function AddEmployeeModal({ onClose, onCreated }) {
  const [form, setForm] = useState({ display_name: '', username: '', email: '', password: '' });
  const [error, setError] = useState('');
  const create = useMutation({
    mutationFn: () => api.createEmployee({
      display_name: form.display_name,
      username: form.username,
      email: form.email,
      password: form.password || undefined,
    }),
    // Merge in the email (the server returns only username + password) so it can be shown once.
    onSuccess: (res) => onCreated({ ...res.credentials, email: form.email }),
    onError: (err) => setError(err.details?.map((d) => `${d.path}: ${d.message}`).join(', ') || err.message),
  });

  const emailValid = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email);

  return (
    <Modal title="Add employee" onClose={onClose}>
      <div className="grid gap-5">
        <Field label="Display name">
          <input
            value={form.display_name}
            onChange={(e) => setForm((f) => ({ ...f, display_name: e.target.value }))}
            placeholder="Priya Sharma"
            className={fieldCls}
          />
        </Field>
        <Field label="Email (used to sign in)">
          <input
            type="email"
            value={form.email}
            onChange={(e) => setForm((f) => ({ ...f, email: e.target.value.trim().toLowerCase() }))}
            placeholder="priya@acme.com"
            className={fieldCls}
          />
        </Field>
        <Field label="Username (display handle)">
          <input
            value={form.username}
            onChange={(e) => setForm((f) => ({ ...f, username: e.target.value.toLowerCase().replace(/[^a-z0-9-_]/g, '') }))}
            placeholder="priya"
            className={`${fieldCls} font-mono`}
          />
        </Field>
        <Field label="Temporary password (leave blank to auto-generate)">
          <input
            value={form.password}
            onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
            placeholder="Auto-generate"
            className={fieldCls}
          />
        </Field>
        {error && <p className="text-sm text-red-600">{error}</p>}
      </div>
      <div className="mt-8 flex justify-end gap-3">
        <button onClick={onClose} className="h-11 rounded-full px-6 text-sm font-semibold text-slate-500 hover:bg-slate-100">Cancel</button>
        <button
          onClick={() => create.mutate()}
          disabled={create.isPending || form.display_name.length < 1 || form.username.length < 3 || !emailValid}
          className="flex h-11 items-center rounded-full bg-slate-900 px-8 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-50"
        >
          {create.isPending ? <Spinner /> : 'Create login'}
        </button>
      </div>
    </Modal>
  );
}

function CredentialsModal({ creds, onClose }) {
  const loginUrl = `${window.location.origin}/login`;
  return (
    <Modal title="Employee login created" onClose={onClose}>
      <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
        <div className="flex items-start gap-2">
          <FiAlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>Copy these now — the password is shown only once. Hand it to your agent; they'll set their own on first sign-in.</span>
        </div>
      </div>
      <div className="mt-5 grid gap-3">
        <CopyRow label="Login page" value={loginUrl} />
        <CopyRow label="Email" value={creds.email} />
        <CopyRow label="Temporary password" value={creds.password} mono />
      </div>
      <div className="mt-8 flex justify-end">
        <button onClick={onClose} className="h-11 rounded-full bg-slate-900 px-8 text-sm font-semibold text-white hover:bg-slate-800">Done</button>
      </div>
    </Modal>
  );
}

function EditEmailModal({ member, onClose, onDone }) {
  const [email, setEmail] = useState(member.email || '');
  const [error, setError] = useState('');
  const save = useMutation({
    mutationFn: () => api.updateEmployee(member.id, { email }),
    onSuccess: onDone,
    onError: (err) => setError(err.message),
  });
  const emailValid = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email);
  return (
    <Modal title={`Change email — ${member.display_name}`} onClose={onClose}>
      <Field label="Email (used to sign in)">
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value.trim().toLowerCase())}
          placeholder="priya@acme.com"
          className={fieldCls}
        />
      </Field>
      {error && <p className="mt-3 text-sm text-red-600">{error}</p>}
      <div className="mt-8 flex justify-end gap-3">
        <button onClick={onClose} className="h-11 rounded-full px-6 text-sm font-semibold text-slate-500 hover:bg-slate-100">Cancel</button>
        <button
          onClick={() => save.mutate()}
          disabled={save.isPending || !emailValid || email === member.email}
          className="flex h-11 items-center rounded-full bg-slate-900 px-8 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-50"
        >
          {save.isPending ? <Spinner /> : 'Save email'}
        </button>
      </div>
    </Modal>
  );
}

function ResetPasswordModal({ member, onClose, onDone }) {
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const reset = useMutation({
    mutationFn: () => api.resetEmployeePassword(member.id, password),
    onSuccess: onDone,
    onError: (err) => setError(err.message),
  });
  return (
    <Modal title={`Reset password — ${member.display_name}`} onClose={onClose}>
      <Field label="New temporary password">
        <input value={password} onChange={(e) => setPassword(e.target.value)} placeholder="At least 8 characters" className={fieldCls} />
      </Field>
      {error && <p className="mt-3 text-sm text-red-600">{error}</p>}
      <p className="mt-3 text-xs text-slate-500">The agent will be asked to set a new password on their next sign-in.</p>
      <div className="mt-8 flex justify-end gap-3">
        <button onClick={onClose} className="h-11 rounded-full px-6 text-sm font-semibold text-slate-500 hover:bg-slate-100">Cancel</button>
        <button
          onClick={() => reset.mutate()}
          disabled={reset.isPending || password.length < 8}
          className="flex h-11 items-center rounded-full bg-slate-900 px-8 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-50"
        >
          {reset.isPending ? <Spinner /> : 'Reset'}
        </button>
      </div>
    </Modal>
  );
}

// --- small shared bits ---
const fieldCls =
  'w-full h-11 rounded-xl border border-slate-400 bg-slate-50 px-4 text-sm font-medium text-slate-700 outline-none focus:border-slate-300 focus:bg-white focus:ring-2 focus:ring-slate-100';

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="mb-2 block text-[10px] font-bold uppercase tracking-wider text-slate-700">{label}</span>
      {children}
    </label>
  );
}

function Modal({ title, children, onClose }) {
  // Backdrop click does NOT close — only the ✕ / Cancel buttons do (avoids losing typed input).
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4 backdrop-blur-sm">
      <div className="w-full max-w-md rounded-3xl bg-white p-8 shadow-2xl animate-dc-rise">
        <div className="mb-6 flex items-center justify-between">
          <h3 className="text-lg font-bold text-slate-900">{title}</h3>
          <button onClick={onClose} className="rounded-full p-1.5 text-slate-400 hover:bg-slate-100"><FiX className="h-5 w-5" /></button>
        </div>
        {children}
      </div>
    </div>,
    document.body
  );
}

function CopyRow({ label, value, mono }) {
  return (
    <div className="flex items-center justify-between rounded-xl border border-slate-100 bg-slate-50 px-4 py-3">
      <div className="min-w-0">
        <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400">{label}</div>
        <div className={`truncate text-sm text-slate-800 ${mono ? 'font-mono' : ''}`}>{value}</div>
      </div>
      <CopyBtn text={value} />
    </div>
  );
}

function CopyBtn({ text }) {
  const [done, setDone] = useState(false);
  return (
    <button
      onClick={() => { navigator.clipboard?.writeText(text); setDone(true); setTimeout(() => setDone(false), 1500); }}
      className="shrink-0 rounded-full p-1.5 text-slate-400 hover:bg-slate-200 hover:text-slate-700"
      title="Copy"
    >
      {done ? <FiCheck className="h-4 w-4 text-emerald-600" /> : <FiCopy className="h-4 w-4" />}
    </button>
  );
}
